import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { FaUpload, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import { optimizeImageForUpload, createThumbnailForUpload } from '../utils/imageUploadUtils';

const SimpleActivityUploader = ({ activityNumber = 2, onUploadComplete }) => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if it's an image file
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setUploadStatus('');
      } else {
        setUploadStatus('❌ Please select an image file (PNG, JPG, JPEG, WEBP)');
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) {
      setUploadStatus('❌ Please select a file and ensure you are logged in');
      return;
    }

    try {
      setUploading(true);
      setUploadStatus('🔄 Uploading activity image...');

      // Debug: Log user info
      console.log('🔍 Upload Debug Info:', {
        user: user.email,
        uid: user.uid,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type
      });

      // Get file extension
      const extension = selectedFile.name.split('.').pop().toLowerCase();
      
      // First, try the exact same path that worked in the test
      const testPath = `pdf-pages/${user.uid}/test_upload.txt`;
      const testRef = ref(storage, testPath);
      const testBlob = new Blob(['Test upload'], { type: 'text/plain' });
      
      console.log('🔍 Testing with exact same path as working test...');
      await uploadBytes(testRef, testBlob);
      console.log('✅ Test upload successful - permissions are working');
      
      // Now try the actual image upload
      const storagePath = `pdf-pages/${user.uid}/page_${activityNumber}.${extension}`;
      const storageRef = ref(storage, storagePath);
      const optimizedFile = await optimizeImageForUpload(selectedFile);
      const thumbnailFile = await createThumbnailForUpload(optimizedFile || selectedFile);
      
      console.log('🔍 Using storage path:', storagePath);
      
      setUploadStatus('🔄 Uploading image to Firebase Storage...');
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, optimizedFile || selectedFile);
      
      console.log('✅ Upload successful:', snapshot);
      
      setUploadStatus('🔄 Getting download URL...');
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      let thumbnailUrl = downloadURL;
      if (thumbnailFile) {
        const thumbRef = ref(storage, `pdf-pages/${user.uid}/thumbnails/page_${activityNumber}.jpg`);
        await uploadBytes(thumbRef, thumbnailFile);
        thumbnailUrl = await getDownloadURL(thumbRef);
      }
      
      console.log('✅ Download URL:', downloadURL);
      
      // Create activity record in Firestore
      setUploadStatus('🔄 Creating activity record...');
      
      const activityData = {
        number: activityNumber,
        title: `Daily Activity ${activityNumber}`,
        description: `Creative activity for developing artistic thinking - Activity ${activityNumber}`,
        content: [
          `Welcome to Activity ${activityNumber}!`,
          `This is a creative activity designed to help you develop your artistic skills and imagination.`,
          `Follow the instructions in the image and create something amazing!`
        ],
        imageUrl: downloadURL,
        thumbnailUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        type: 'daily_activity',
        difficulty: 'Beginner',
        duration: '20 minutes',
        materials: 'Paper, pencils, crayons, markers, or any art supplies you prefer',
        category: 'Creative Expression'
      };

      console.log('🔍 Activity Data:', activityData);

      // Add to Firestore
      const activityRef = await addDoc(collection(db, 'activities'), activityData);
      
      console.log('✅ Firestore document created:', activityRef.id);
      
      setUploadStatus(`
        ✅ Activity ${activityNumber} uploaded successfully!
        📁 File: ${selectedFile.name}
        📍 Storage Path: ${storagePath}
        🗄️ Firestore ID: ${activityRef.id}
        🎯 This will appear as Activity ${activityNumber} in your frontend
      `);

      // Clear file input
      setSelectedFile(null);
      document.getElementById('fileInput').value = '';

      // Call callback if provided
      if (onUploadComplete) {
        onUploadComplete(downloadURL, activityNumber, activityRef.id);
      }

    } catch (error) {
      console.error('❌ Upload error details:', {
        error: error.message,
        code: error.code,
        stack: error.stack,
        user: user?.email,
        uid: user?.uid
      });
      
      setUploadStatus(`❌ Upload failed: ${error.message}\n\nDebug Info:\n- User: ${user?.email}\n- UID: ${user?.uid}\n- Error Code: ${error.code}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#d97706';
    e.currentTarget.style.background = '#fde68a';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#f59e0b';
    e.currentTarget.style.background = '#fef3c7';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#f59e0b';
    e.currentTarget.style.background = '#fef3c7';
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setUploadStatus('');
      } else {
        setUploadStatus('❌ Please select an image file (PNG, JPG, JPEG, WEBP)');
      }
    }
  };

  if (!user) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <FaTimes className="text-red-500 text-2xl mx-auto mb-2" />
        <p className="text-red-700">Please log in to upload activity images</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-amber-800 mb-2">
          🎨 Upload Activity {activityNumber}
        </h2>
        <p className="text-amber-600">
          Upload your activity image to make it available in the frontend
        </p>
      </div>

      <div 
        className="border-2 border-dashed border-amber-300 rounded-lg p-6 text-center mb-4 bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('fileInput').click()}
      >
        <input
          id="fileInput"
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {selectedFile ? (
          <div>
            <FaCheck className="text-green-500 text-3xl mx-auto mb-2" />
            <p className="text-green-700 font-semibold">File Selected:</p>
            <p className="text-green-600 text-sm">{selectedFile.name}</p>
            <p className="text-amber-600 text-xs mt-1">
              Click to change file or drag & drop a new one
            </p>
          </div>
        ) : (
          <div>
            <FaUpload className="text-amber-500 text-3xl mx-auto mb-2" />
            <p className="text-amber-700 font-semibold">Click to select or drag & drop</p>
            <p className="text-amber-600 text-sm">PNG, JPG, JPEG, WEBP</p>
          </div>
        )}
      </div>

      {selectedFile && (
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 disabled:from-amber-300 disabled:to-amber-300 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <FaSpinner className="animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <FaUpload />
              Upload Activity {activityNumber}
            </>
          )}
        </button>
      )}

      {uploadStatus && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          uploadStatus.includes('✅') 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : uploadStatus.includes('❌') 
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          <div dangerouslySetInnerHTML={{ __html: uploadStatus.replace(/\n/g, '<br>') }} />
        </div>
      )}

      <div className="mt-4 text-xs text-amber-600 text-center">
        <p>File will be stored as: <code>pdf-pages/{user.uid}/page_{activityNumber}.{selectedFile?.name.split('.').pop()}</code></p>
      </div>
    </div>
  );
};

export default SimpleActivityUploader;
